import React, { ReactElement, useState } from 'react';
import { objectUtils } from '@codametrix/ui-common';
import classnames from 'classnames';
import { Conditional } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';

export type NotesNavbarProps = {
  activeNote: CMxCommonApp.DecoratedNote;
  documentMetadata?: CMxAPI.DocumentMetadata[];
  onSelect?: (docMetadata: CMxAPI.DocumentMetadata) => void;
  isIPC?: boolean;
  capabilities?: Record<string | number | symbol, CMxCommonApp.Capability>;
  children?: ReactElement;
  grouped?: boolean;
  showAddAttachDocsButton: boolean;
  showAttachDocsModal: () => void;
};

const DEFAULT_PROPS = {
  documentMetadata: [],
  grouped: false,
  onSelect: (docMetadata: CMxAPI.DocumentMetadata) => {}
};

const DocCategories: {
  [key: string]: string;
} = {
  DFT: 'DFTs',
  DFT_IN: 'Inbound DFTs',
  DFT_OUT: 'Outbound DFTs',
  NTE: 'Notes',
  NOTE: 'Notes',
  HL7: 'HL7 Messages',
  DFT_EHR: 'DFT EHRs'
};

const NotesNavbarv2 = (passed: NotesNavbarProps) => {
  const props = { ...DEFAULT_PROPS, ...passed };
  const {
    documentMetadata,
    onSelect,
    activeNote,
    isIPC,
    capabilities,
    grouped,
    showAddAttachDocsButton,
    showAttachDocsModal
  } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const documentsPerPage = 5; // Number of documents to show per page

  //@ts-ignore
  const prepped = (documentMetadata || []).map((doc) => {
    const rawCategory =
      doc.mimeType === 'text/html' ? 'HTML' : doc.documentCategory;
    return {
      ...doc,
      documentCategory: DocCategories[rawCategory] || rawCategory
    };
  });

  let preppedDocs;

  if (grouped) {
    const sortGroupedDocs = (a: string, b: string) => {
      if (a === b) return 0;
      if (a === 'HL7 Messages' || a === 'Notes') return -1;
      if (b === 'HL7 Messages' || b === 'Notes') return 1;
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    };
    preppedDocs = objectUtils.groupBy(prepped, 'documentCategory');
    Object.keys(preppedDocs).sort(sortGroupedDocs);
  } else {
    preppedDocs = prepped;
  }

  const buildDocList = (docs) => {
    return docs.map((document, idx) => {
      const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        onSelect(document);
      };

      const isActive = activeNote?.id === document.id;
      const styleClasses = classnames([
        { 'is-active': isActive },
        'navbar-link'
      ]);

      return (
        <li key={document.documentGuid.uuid} className={styleClasses}>
          <a
            href="#/something"
            onClick={onClick}
            className="document-list-item">
            <span className="document-name">
              {document.documentName ?? 'Name N/A'}
            </span>
            <span className="document-description">
              {document.documentDescription}
            </span>
            <span className="document-status">{document.documentStatus}</span>
          </a>
        </li>
      );
    });
  };

  const displayDocs = (docs) => {
    const startIndex = currentIndex * documentsPerPage;
    const endIndex = startIndex + documentsPerPage;
    return docs.slice(startIndex, endIndex);
  };

  const handlePrevClick = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextClick = () => {
    const maxIndex = Math.ceil(preppedDocs.length / documentsPerPage) - 1;
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  let content;
  if (grouped) {
    content = Object.keys(preppedDocs).map((key) => {
      const docs = preppedDocs[key];
      const items = buildDocList(displayDocs(docs));

      return (
        <div key={key}>
          <span className="note-doc-category" style={{ display: 'none' }}>
            {docs[0].documentCategory}
          </span>
          <ul className="note-navbar-list">{items}</ul>
        </div>
      );
    });
  } else {
    const listClass = classnames('note-navbar-list', {
      'justify-flex-start': displayDocs(preppedDocs).length < documentsPerPage
    });

    content = (
      <div>
        <ul className={listClass}>{buildDocList(displayDocs(preppedDocs))}</ul>
      </div>
    );
  }

  return (
    <div className="notes-navbar">
      {documentMetadata && documentMetadata.length > documentsPerPage && (
        <div className="navigation">
          {currentIndex > 0 && (
            <button className="nav-left" onClick={handlePrevClick}>
              <CMXIcon svgIconName="BiomeLeftArrow" svgIconSize="20" />
            </button>
          )}
          <div className="content-container">{content}</div>
          <Conditional on={showAddAttachDocsButton}>
            <button className="add-button" onClick={showAttachDocsModal}>
              <CMXIcon svgIconName="BiomePlusCircle" svgIconSize="20" />
              Add
            </button>
          </Conditional>
          {currentIndex <
            Math.ceil(preppedDocs.length / documentsPerPage) - 1 && (
            <button className="nav-right" onClick={handleNextClick}>
              <CMXIcon svgIconName="BiomeRightArrow" svgIconSize="20" />
            </button>
          )}
        </div>
      )}
      {!(documentMetadata && documentMetadata.length > documentsPerPage) && (
        <div className="navigation">
          <div className="content-container">{content}</div>
          <Conditional on={showAddAttachDocsButton}>
            <button className="add-button" onClick={props.showAttachDocsModal}>
              <CMXIcon svgIconName="BiomePlusCircle" svgIconSize="20" />
              Add
            </button>
          </Conditional>
        </div>
      )}
      {props.children}
    </div>
  );
};

export { NotesNavbarv2 };
